import Image from 'next/image'
import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import clsx from 'clsx'

import { SubnetType } from '@infura/key-service-sdk/lib/v1/proto/service_plan/v1/networks'
import { Button } from '@infura/packages.ui/Button'
import { Codeblock } from '@infura/packages.ui/Codeblock'
import { Heading } from '@infura/packages.ui/Heading'
import { Input } from '@infura/packages.ui/Input'
import { Select } from '@infura/packages.ui/Select'
import { Text } from '@infura/packages.ui/Text'
import { V1Network, V1SubnetType } from '@infura/packages.uks'

import { buildOptionsList } from 'components/gettingStartedTour/StepViewApiKey/data'
import { TNetworksOptionsList, TUserKeysList } from 'components/gettingStartedTour/StepViewApiKey/types'
import getDescription from 'components/gettingStartedTour/StepViewApiKey/useDescription'
import { GST_IS_SYNC_REQUEST } from 'components/gettingStartedTour/data'
import { IStepsComponent } from 'components/gettingStartedTour/types'

import { useUksNetworks } from 'hooks/useUksNetworks'
import useUser from 'hooks/useUser'

import { uks } from 'lib/api/uks'

const StepViewApiKey = ({
  apiKeySelected,
  handleNextStep,
  networkSelected,
  revalidateUksKeys,
  setApiKeySelected,
  setNetworkSelected,
  setSubnetUrl,
  subnetUrl,
  userKeys
}: IStepsComponent) => {
  const { user } = useUser()
  const [isLoading, setIsLoading] = useState(false)
  const [networksOptionsList, setNetworksOptionsList] = useState<TNetworksOptionsList>([])
  const [userKeysList, setUserKeysList] = useState<TUserKeysList>([])
  const [keyToCreate, setKeyToCreate] = useState('')
  const [showKeyAndNetwork, setShowKeyAndNetwork] = useState<boolean>(!!(apiKeySelected && networkSelected))
  const [subnetName, setSubnetName] = useState<null | string>(null)
  const apiKeysNumber = useMemo(() => Object.values(userKeys).length, [userKeys])

  const {
    activeNetworkEntries,
    allNetworkEntries,
    isLoading: isNetworksLoading,
    revalidate: revalidateUksNetworks
  } = useUksNetworks(apiKeySelected)
  const { description, title } = getDescription(
    apiKeysNumber,
    apiKeySelected,
    networkSelected,
    subnetName,
    showKeyAndNetwork
  )

  useEffect(() => {
    if (apiKeysNumber > 1) {
      setUserKeysList(buildOptionsList('user', (Object as any).values(userKeys)))
    }
  }, [userKeys])

  useEffect(() => {
    if (!isNetworksLoading) {
      if (activeNetworkEntries.length === 1) {
        setNetworkSelected(activeNetworkEntries?.[0]?.[0] as string)
        setShowKeyAndNetwork(true)
      } else if (activeNetworkEntries.length > 1) {
        setNetworkSelected(null)
        setShowKeyAndNetwork(false)
        setNetworksOptionsList(buildOptionsList('network', activeNetworkEntries))
      } else {
        setNetworkSelected(null)
        setShowKeyAndNetwork(false)
        setNetworksOptionsList(buildOptionsList('network', allNetworkEntries))
      }
    }
  }, [isNetworksLoading])

  useEffect(() => {
    if (networkSelected && apiKeySelected) {
      const networksList = activeNetworkEntries?.length > 0 ? activeNetworkEntries : allNetworkEntries
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      const [, { subnets }]: Array<any> = networksList.find(([networkKey]) => networkKey === networkSelected)
      let subnet
      if (subnets.SUBNET_MAINNET) {
        subnet = subnets.SUBNET_MAINNET
        setSubnetName('MAINNET')
      } else {
        const subnetKey = Object.keys(subnets as V1Network)[0]
        subnet = subnets[subnetKey]
        setSubnetName(subnetKey.replace('SUBNET_', ''))
      }
      const url =
        process.env.ENVIRONMENT === 'production' ? subnet.url : subnet.url.replace('infura.io', 'dev.infura.org')
      const version = subnet.schemes?.https?.version || 'v3'

      setSubnetUrl(`${url}/${version}`)
    }
  }, [networkSelected, apiKeySelected, activeNetworkEntries, allNetworkEntries])

  const handleNextOrCreate = async () => {
    setIsLoading(true)

    if (showKeyAndNetwork) {
      handleNextStep()
      return
    }

    if (networkSelected) {
      const allAvailableSubnets = allNetworkEntries.find(([networkKey]) => networkKey === networkSelected)?.[1]?.subnets
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      const subnetsToAdd: Array<V1SubnetType> = Object.keys(allAvailableSubnets).map(subnetKey => SubnetType[subnetKey])
      try {
        if (keyToCreate && !localStorage.getItem(GST_IS_SYNC_REQUEST)) {
          localStorage.setItem(GST_IS_SYNC_REQUEST, 'true')
          const { data } = await uks.projectMethodsUpsert({
            name: keyToCreate,
            settings: { basic: { description: '' } },
            // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
            userId: user.id
          })
          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
          await uks.securityMethodsGenerate(data.result.id, {})
          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
          await uks.projectMethodsUpdateSubnets(data.result.id, subnetsToAdd, networkSelected)
          await revalidateUksKeys()
          await revalidateUksNetworks()
          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
          setApiKeySelected(data.result.id)
          setShowKeyAndNetwork(true)
          localStorage.removeItem(GST_IS_SYNC_REQUEST)
        } else if (apiKeySelected && activeNetworkEntries?.length === 0) {
          await uks.projectMethodsUpdateSubnets(apiKeySelected, subnetsToAdd, networkSelected)
          await revalidateUksNetworks()
        }
      } catch (e) {
        await revalidateUksKeys()
        await revalidateUksNetworks()
        localStorage.removeItem(GST_IS_SYNC_REQUEST)
      }
    }

    if (subnetUrl && !showKeyAndNetwork) {
      setShowKeyAndNetwork(true)
    }
    setIsLoading(false)
  }

  return (
    <div className='m-auto w-full max-w-screen-2xl'>
      <div className='flex content-center xl:gap-16'>
        <div className={clsx('w-full content-center', !showKeyAndNetwork && 'xl:!w-1/2')}>
          {!showKeyAndNetwork ? (
            <Image alt='Select key' className='mb-4' height={41} src={`/images/no-api-keys.svg`} width={41} />
          ) : null}
          <Heading as='h4'>{title}</Heading>
          <Text alternative className='mb-4'>
            {description}
          </Text>

          {showKeyAndNetwork ? (
            <div>
              <div className='mb-2 inline-flex h-6 items-center justify-start gap-2'>
                <div className='flex items-start justify-start gap-2.5'>
                  <div className='text-text-default text-sm font-medium'>API key</div>
                </div>
              </div>
              {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
              <Codeblock className='mb-4' hasCopyButton theme='dark' type='single-line'>
                {apiKeySelected}
              </Codeblock>
              <div className='mb-2 inline-flex h-6 items-center justify-start gap-2'>
                <div className='flex items-start justify-start gap-2.5'>
                  <div className='text-text-default text-sm font-medium'>HTTPS</div>
                </div>
              </div>
              <Codeblock className='mb-12' hasCopyButton theme='dark' type='single-line'>
                {`https://${subnetUrl}/${apiKeySelected}`}
              </Codeblock>
            </div>
          ) : (
            <>
              {apiKeysNumber === 0 ? (
                <Input
                  className={clsx('mb-4', isLoading && '!border !border-solid !bg-gray-100 !opacity-100')}
                  disabled={isLoading}
                  label={
                    <Text as='p' className='m-0 text-black' theme='sm'>
                      <span>Name your first api key </span>
                      <span className='text-primary ml-1'>*</span>
                    </Text>
                  }
                  name='Name your first api key*'
                  onChange={event => setKeyToCreate((event.target as HTMLInputElement).value)}
                  placeholder='API key name'
                  type='text'
                />
              ) : null}

              {apiKeysNumber > 1 ? (
                <Select
                  buttonStyles={clsx('pl-4', isLoading && '!bg-gray-100')}
                  className='mb-4 w-full'
                  disabled={isLoading}
                  label={
                    <Text as='p' className='m-0 text-black' theme='sm'>
                      <span>Select api key </span>
                      <span className='text-primary ml-1'>*</span>
                    </Text>
                  }
                  onChange={option => {
                    setApiKeySelected(option.value)
                  }}
                  options={userKeysList}
                  placeholder='Select API Key'
                  showDescription
                  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                  value={null}
                />
              ) : null}

              <Select
                buttonStyles={'pl-4'}
                className='w-full'
                disabled={isLoading || !(apiKeySelected || keyToCreate) || isNetworksLoading}
                onChange={option => {
                  setNetworkSelected(option.value)
                }}
                options={networksOptionsList}
                placeholder={
                  isNetworksLoading ? (
                    <>
                      <FontAwesomeIcon className='fa-fw fa-spin mr-1' icon={faSpinnerThird} />{' '}
                      <span>Select Network</span>
                    </>
                  ) : (
                    'Select Network'
                  )
                }
                // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                value={null}
              />
              <div className='text-text-default mb-4 mt-2 text-sm font-normal'>You can always add more later</div>
            </>
          )}

          <Button
            className='mb-4 h-max w-full md:mb-0 md:w-auto'
            data-testid='getting-started-next'
            disabled={isLoading || !(apiKeySelected || keyToCreate) || !networkSelected}
            onClick={handleNextOrCreate}
          >
            {isLoading ? (
              <span>
                <FontAwesomeIcon className='fa-fw fa-spin mr-1' icon={faSpinnerThird} />
              </span>
            ) : null}
            <span>{apiKeysNumber === 0 ? 'Create' : 'Next'}</span>
          </Button>
        </div>

        {!showKeyAndNetwork ? (
          <div className='hidden items-center justify-center xl:!flex xl:w-1/2'>
            <Image alt='Empty states' height={338} quality={100} src={`/images/emptyStates.png`} width={338} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default StepViewApiKey
